import { useCurrentFrame, interpolate, Easing } from 'remotion';

// Constants
import { CLAMP_BOTH } from '@/constants';

export interface CrossZoomTransitionConfig {
  /** Frame when the transition should start */
  startFrame: number;
  /** Duration of the transition in frames */
  duration: number;
  /** Maximum scale for the outgoing scene before disappearing */
  outgoingMaxScale?: number;
  /** Initial scale for the incoming scene before zooming in */
  incomingInitialScale?: number;
  /** Custom easing function */
  easing?: (t: number) => number;
}

export interface CrossZoomTransitionResult {
  /** Progress of the transition (0 to 1) */
  progress: number;
  /** Style for the outgoing scene (Scene 6) */
  outgoingStyle: React.CSSProperties;
  /** Style for the incoming scene (Scene 7) */
  incomingStyle: React.CSSProperties;
  /** Whether the transition has started */
  isTransitioning: boolean;
  /** Whether the transition has completed */
  isComplete: boolean;
}

/**
 * Creates a Cross Zoom (Dolly Zoom) transition effect between two scenes.
 */
export const useCrossZoomTransition = ({
  startFrame,
  duration,
  outgoingMaxScale = 8,
  incomingInitialScale = 5,
  easing = Easing.inOut(Easing.cubic),
}: CrossZoomTransitionConfig): CrossZoomTransitionResult => {
  const frame = useCurrentFrame();

  // Overall transition progress
  const progress = interpolate(
    frame,
    [startFrame, startFrame + duration],
    [0, 1],
    {
      ...CLAMP_BOTH,
      easing,
    },
  );

  // --- Outgoing scene (Scene 6): zoom in + fade out + blur ---
  const outgoingScale = interpolate(
    progress,
    [0, 1],
    [1, outgoingMaxScale],
    CLAMP_BOTH,
  );

  const outgoingOpacity = interpolate(
    progress,
    [0, 0.5, 0.75],
    [1, 0.6, 0],
    CLAMP_BOTH,
  );

  const outgoingBlur = interpolate(progress, [0, 0.6], [0, 20], CLAMP_BOTH);

  const outgoingRotation = interpolate(progress, [0, 1], [0, -3], CLAMP_BOTH);

  // --- Incoming scene (Scene 7): zoom out + fade in + unblur ---
  const incomingScale = interpolate(
    progress,
    [0, 1],
    [incomingInitialScale, 1],
    CLAMP_BOTH,
  );

  const incomingOpacity = interpolate(
    progress,
    [0.25, 0.5, 1],
    [0, 0.4, 1],
    CLAMP_BOTH,
  );

  const incomingBlur = interpolate(progress, [0.3, 1], [20, 0], CLAMP_BOTH);

  const incomingRotation = interpolate(progress, [0, 1], [3, 0], CLAMP_BOTH);

  return {
    progress,
    outgoingStyle: {
      transform: `scale(${outgoingScale}) rotate(${outgoingRotation}deg)`,
      opacity: outgoingOpacity,
      filter: `blur(${outgoingBlur}px)`,
    },
    incomingStyle: {
      transform: `scale(${incomingScale}) rotate(${incomingRotation}deg)`,
      opacity: incomingOpacity,
      filter: `blur(${incomingBlur}px)`,
    },
    isTransitioning: progress > 0 && progress < 1,
    isComplete: progress >= 1,
  };
};
